import React, { useState } from 'react';
import { Alert, Button, Content, Modal, ModalBody, ModalFooter, ModalHeader } from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { AdminCertificatePlatform } from '../../../types/models/AdminCertificatePlatform';
import type { ApiResponseAdminCertificatePlatformDeleted } from '../../../types/api/ApiResponseAdminCertificatePlatform';

interface CertificatePlatformDeleteModalProps {
  platform: AdminCertificatePlatform;
  onClose: () => void;
  /** Drop the deleted row from the parent's list. */
  onDeleted: (id: number) => void;
}

/**
 * Two-step optimistic delete for an admin certificate platform. The list row only carries the
 * TOTAL certificate_count — the attributed/orphan split lives in the backend's delete errors — so
 * the flow tries the plain DELETE first and reacts to the outcome:
 *
 *   1. Confirm → DELETE without the flag. Clean success removes the row.
 *   2. Certificates ATTRIBUTED to tenants always block (even with the flag): the backend message
 *      is terminal — shown in the modal, nothing more to do from here.
 *   3. ORPHANED (unattributed cache) rows block by default: the backend's "pass ?cascade_orphans=1"
 *      message escalates the modal to a second confirmation, and only that explicit confirmation
 *      retries with ?cascade_orphans=1 (deleting platform + orphans atomically).
 */
function CertificatePlatformDeleteModal({ platform, onClose, onDeleted }: CertificatePlatformDeleteModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  // The backend's verbatim orphan-blocker message (it names the exact count); non-null once the
  // first attempt bounced and the modal escalated to the cascade confirmation.
  const [cascadeMessage, setCascadeMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (cascadeOrphans: boolean) => {
    setIsDeleting(true);
    setError(null);
    try {
      const response = await apiFetch(
        `/admin/certificate-platforms/${platform.id}${cascadeOrphans ? '?cascade_orphans=1' : ''}`,
        { method: 'DELETE' },
      );
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseAdminCertificatePlatformDeleted | null;

      if (body?.status === 'success' && body.data?.deleted) {
        const { orphan_certificates_deleted } = body.data.deleted;
        addNotification({
          title: 'Certificate platform deleted',
          srTitle: 'Success alert',
          description:
            orphan_certificates_deleted > 0
              ? `${platform.name} was deleted, along with ${orphan_certificates_deleted} unattributed certificate(s).`
              : `${platform.name} was deleted.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onDeleted(platform.id);
        onClose();
        return;
      }

      const platformBlocker = typeof body?.data?.platform === 'string' ? body.data.platform : null;

      // Orphan blocker → escalate to the cascade confirmation instead of failing. (Checked with
      // the attributed case excluded first: BOTH backend messages mention "cascade_orphans", but
      // only the orphan one says "pass ?cascade_orphans=1".)
      if (
        platformBlocker !== null &&
        !platformBlocker.includes('attributed to tenants') &&
        platformBlocker.includes('pass ?cascade_orphans=1')
      ) {
        setCascadeMessage(platformBlocker);
        return;
      }

      const message = platformBlocker ?? body?.message ?? 'Could not delete the certificate platform. Please try again.';
      setError(message);
      addNotification({
        title: 'Could not delete certificate platform',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(message);
      addNotification({
        title: 'Could not delete certificate platform',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const isCascadeStep = cascadeMessage !== null;

  return (
    <Modal variant="small" isOpen onClose={onClose}>
      <ModalHeader title="Delete certificate platform" titleIconVariant="warning" />
      <ModalBody>
        {error !== null && (
          <Alert variant="danger" isInline title="Delete failed" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        {isCascadeStep ? (
          <>
            <Alert variant="warning" isInline title="Unattributed certificates on this platform" className="pf-v6-u-mb-md">
              {cascadeMessage}
            </Alert>
            <Content>
              Deleting <strong>{platform.name}</strong> will also permanently delete its unattributed certificate
              cache rows. Certificates attributed to tenants are never removed this way.
            </Content>
          </>
        ) : (
          <Content>
            Delete <strong>{platform.name}</strong>? The Manager will no longer sync certificates from this endpoint.
            {platform.certificate_count > 0 && (
              <>
                {' '}
                It currently holds <strong>{platform.certificate_count}</strong> synced certificate(s).
              </>
            )}
          </Content>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="danger"
          onClick={() => void handleDelete(isCascadeStep)}
          isLoading={isDeleting}
          isDisabled={isDeleting}
        >
          {isCascadeStep ? 'Delete platform and orphaned certificates' : 'Delete'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isDeleting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default CertificatePlatformDeleteModal;
